/* eslint-disable */
import { jwtDecode } from "jwt-decode"
import mitt from "mitt"

import type { Config, GPTModelItem } from "@/api/models"
import type { JwtPayload } from "jwt-decode"

import { configApi, getSettingsApi, setSettingsApi } from "@/api/api"
import { RetrievalMode } from "@/api/models"
import { getStoredEasyAuthInfo } from "@/api/auth-api"
import { logger } from "@/lib/logger"
import { setMemoryBackend } from "@/stores/memory-backend-store"

interface EasyAuthClaim {
  typ: string
  val: string
}

interface EasyAuthInfo {
  user_id: string
  provider_name: string
  user_claims?: EasyAuthClaim[]
  expires_on?: string
  [key: string]: unknown
}

interface Version {
  // System prompt version
  id: string
  timestamp: number
  text: string
  name: string
}

interface RetrivalItem {
  DisplayName: string
  mode: RetrievalMode
}

const RetrivalPresets: { [key: string]: RetrivalItem } = {
  Hybrid: { DisplayName: "Vektorer + Text (Hybrid)", mode: RetrievalMode.Hybrid },
  Vectors: { DisplayName: "Vektorer", mode: RetrievalMode.Vectors },
  Text: { DisplayName: "Text", mode: RetrievalMode.Text },
}

export type ActiveSettings = {
  chatModel: { [session_type: string]: GPTModelItem }
  chatStream: boolean
  chatTemperature: number
  chatFollowupQuestions: boolean
  demoMode: boolean

  chatRagSemanticRanking: boolean
  chatRagContextSummary: boolean
  chatRagRetrievalCount: number
  chatRagRetrievalMode: RetrivalItem

  languageCode: string

  translationDictionary: { [key: string]: string }
  translationInstructions: string
  targetLanguage: string
  targetLanguageOptions: string[]

  systemPrompts: Version[]
  currentSystemPromptId: string
}

export interface ExtendedJwtPayload extends JwtPayload {
  email?: string
  name?: string
  preferred_username?: string
  oid?: string
  tid?: string
  ver?: string
  unique_name?: string
  groups?: string[]
  // Add any other fields you need
}

export class SettingsService {
  private static instance: SettingsService

  public static initializedEvent = mitt()

  public static createInstance(
    token: string,
    usingMsal: boolean = false,
    beforeInitialize?: () => void
  ) {
    if (SettingsService.instance) {
      logger.debug("SettingsService instance already exists, skipping...")
      beforeInitialize?.()
      return
    }
    SettingsService.instance = new SettingsService()
    // Allocate and expose the initial bearer before registration. Network boot
    // must start only after the authenticated session owns its dispatches.
    SettingsService.instance.token = token
    SettingsService.instance.usingMsalBackup = usingMsal
    beforeInitialize?.()
    SettingsService.instance.initialize(token, usingMsal)
  }

  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      throw new Error("SettingsService instance not created yet!")
    }
    return SettingsService.instance
  }

  /**
   * Publishes a freshly acquired bearer so both HTTP transports pick it up
   * (#2904). Returns whether the write actually stuck — the refresh coordinator
   * treats a failed write-through as a failed refresh, because a "successful"
   * refresh nobody can read just means the next request resends the dead token.
   * Only `token` is touched; every other preference is left alone.
   */
  public static publishToken(token: string): boolean {
    if (!SettingsService.instance || !token) return false
    SettingsService.instance.token = token
    return SettingsService.instance.token === token
  }

  /** The bearer currently held, or undefined before the instance exists. */
  public static peekToken(): string | undefined {
    return SettingsService.instance?.token
  }

  // `initializedEvent` fires exactly once. Callers that subscribe afterwards
  // never hear it, so they need this to tell "not ready yet" from "already
  // finished before I looked".
  public static isInitialized(): boolean {
    return SettingsService.instance?.initialized === true
  }
  private async initialize(token: string, usingMsal: boolean) {
    logger.debug("Initializing SettingsService...")

    this.usingMsalBackup = usingMsal
    this.token = token

    // Check if this is a placeholder token for EasyAuth
    if (token === "easyauth-token") {
      logger.debug("Using EasyAuth, fetching user info from /.auth/me")
      try {
        // First check if we have stored EasyAuth info
        const storedAuthInfo = getStoredEasyAuthInfo()

        if (storedAuthInfo && storedAuthInfo.user_id) {
          logger.debug("Using stored EasyAuth info")
          this.setClaimsFromEasyAuthInfo(storedAuthInfo)
        } else {
          // If no stored info, fetch from /.auth/me endpoint
          const rootUrl = window.location.origin
          const response = await fetch(`${rootUrl}/.auth/me`)

          if (response.ok) {
            const data = await response.json()

            if (Array.isArray(data) && data.length > 0) {
              const authInfo = data[0]
              this.setClaimsFromEasyAuthInfo(authInfo)
            } else {
              logger.warn("EasyAuth response missing user information, using default claims")
              this.setDefaultEasyAuthClaims()
            }
          } else {
            logger.warn(
              `Failed to fetch EasyAuth user info, status: ${response.status}, using default claims`
            )
            this.setDefaultEasyAuthClaims()
          }
        }
      } catch (error) {
        logger.error("Error fetching EasyAuth user info", error as Error)
        this.setDefaultEasyAuthClaims()
      }
    } else {
      // Regular JWT token, decode it
      this.claims = jwtDecode<ExtendedJwtPayload>(token)
    }

    await this.getSettings()

    logger.debug("SettingsService initialized!")
    this.initialized = true
    SettingsService.initializedEvent.emit("initialized")
  }

  private setClaimsFromEasyAuthInfo(authInfo: EasyAuthInfo) {
    let expTime: number
    if (authInfo.expires_on) {
      try {
        const expiresDate = new Date(authInfo.expires_on)
        expTime = Math.floor(expiresDate.getTime() / 1000) // Convert to seconds for JWT format
        logger.debug(
          `Using expiration time from EasyAuth: ${new Date(expTime * 1000).toISOString()}`
        )
      } catch (error) {
        logger.warn(
          "Failed to parse expires_on from EasyAuth, using default expiration",
          error as Error
        )
        expTime = Math.floor(Date.now() / 1000) + 3600 // 1 hour from now as fallback
      }
    } else {
      expTime = Math.floor(Date.now() / 1000) + 3600 // 1 hour from now as fallback
    }

    // Find the name claim - use the 'name' claim directly
    const name =
      authInfo.user_claims?.find((claim) => claim.typ === "name")?.val ||
      authInfo.user_id ||
      "Unknown User"

    // Find the email claim - prefer preferred_username or user_id
    const email =
      authInfo.user_claims?.find((claim) => claim.typ === "preferred_username")?.val ||
      authInfo.user_claims?.find(
        (claim) =>
          claim.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
      )?.val ||
      authInfo.user_id ||
      "unknown@example.com"

    // Find the unique identifier
    const oid =
      authInfo.user_claims?.find(
        (claim) => claim.typ === "http://schemas.microsoft.com/identity/claims/objectidentifier"
      )?.val || authInfo.user_claims?.find((claim) => claim.typ === "oid")?.val

    // Find the tenant ID
    const tid =
      authInfo.user_claims?.find(
        (claim) => claim.typ === "http://schemas.microsoft.com/identity/claims/tenantid"
      )?.val || authInfo.user_claims?.find((claim) => claim.typ === "tid")?.val

    // Set the claims with all the extracted information
    this.claims = {
      exp: expTime,
      iat: Math.floor(Date.now() / 1000), // Current time in seconds
      iss: "easyauth",
      sub: authInfo.user_id || "easyauth-user",
      name: name,
      email: email,
      unique_name: authInfo.user_id || "easyauth-user",
      preferred_username:
        authInfo.user_claims?.find((claim) => claim.typ === "preferred_username")?.val || email,
      oid: oid,
      tid: tid,
    }

    logger.debug("EasyAuth user info set successfully", this.claims)
  }

  // Helper method to set default EasyAuth claims when /.auth/me fails
  private setDefaultEasyAuthClaims() {
    const defaultUserId = "easyauth-user"
    const defaultEmail = "anonymous@example.com"
    const defaultName = "Anonymous User"

    this.claims = {
      exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
      iat: Math.floor(Date.now() / 1000),
      iss: "easyauth",
      sub: defaultUserId,
      name: defaultName,
      email: defaultEmail,
      unique_name: defaultUserId,
      preferred_username: defaultEmail,
    }

    logger.debug("Default EasyAuth claims set", this.claims)
  }

  initialized: boolean = false

  usingMsalBackup: boolean = false
  claims = {} as ExtendedJwtPayload
  token: string = ""

  settings: Config = {} as Config
  _activeSettings: ActiveSettings | undefined

  async resetSettingsToDefault() {
    this._activeSettings = this.getDefaultSettings()
    await setSettingsApi(this._activeSettings, this.token)
  }

  private getDefaultSettings(): ActiveSettings {
    return {
      chatModel: {},
      chatStream: true,
      chatTemperature: 0.7,
      chatFollowupQuestions: true,
      chatRagSemanticRanking: true,
      chatRagContextSummary: false,
      chatRagRetrievalCount: 20,
      chatRagRetrievalMode: RetrivalPresets.Hybrid,
      languageCode: "sv",

      translationDictionary: {},
      translationInstructions: "",
      targetLanguage: "English",
      targetLanguageOptions: ["Svenska", "English", "Norsk", "Suomi"],

      systemPrompts: [],
      currentSystemPromptId: "",

      demoMode: false,
    }
  }

  async getActiveSettings(): Promise<ActiveSettings> {
    // Ensure base settings are loaded (guards against race with initialize())
    if (!this.settings || !Array.isArray(this.settings.models)) {
      try {
        await this.getSettings()
      } catch (e) {
        logger.warn("Failed to load base settings before getActiveSettings", e as Error)
      }
    }
    if (!this._activeSettings) {
      const res = await getSettingsApi(this.token)
      if (res && this.isValidSettings(res)) {
        this._activeSettings = res
      } else {
        // Settings invalid or not found, use defaults.
        this._activeSettings = this.getDefaultSettings()
        this.setActiveSettings(this._activeSettings)
      }
    }
    this._activeSettings.systemPrompts = this._activeSettings.systemPrompts || []
    return this._activeSettings
  }

  private isValidSettings(incomingSettings: ActiveSettings): boolean {
    const { chatModel } = incomingSettings

    // Check if model ids are valid
    const modelIds = (this.settings.models ?? []).map((model) => model.id)
    for (const key in chatModel) {
      if (chatModel.hasOwnProperty(key)) {
        const modelId = chatModel[key].id

        if (!modelIds.includes(modelId)) {
          return false
        }
      }
    }

    if ((incomingSettings.targetLanguageOptions ?? []).length < 4) {
      return false
    }

    return true
  }

  private timeoutId: number | null = null

  async setActiveSettings(value: ActiveSettings) {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
    }

    this.timeoutId = window.setTimeout(async () => {
      this._activeSettings = value
      await setSettingsApi(this._activeSettings, this.token)
    }, 500)
  }

  async getSettings() {
    this.settings = await configApi(this.token)
    setMemoryBackend(this.settings.memoryBackend)
  }

  async getActiveModel(
    session_type: string
  ): Promise<{ allowedModules: GPTModelItem[]; activeModel: GPTModelItem }> {
    const activeSettings = await this.getActiveSettings()
    const allowedModules = this.getModelsforModule(session_type)

    const activeModel = activeSettings.chatModel[session_type]
    if (!activeModel) {
      const defaultModel = allowedModules.find((m) => m.default === true)
      const anyModel = (this.settings.models ?? [])[0]
      activeSettings.chatModel[session_type] = defaultModel ||
        allowedModules[0] ||
        anyModel || {
          id: "",
          type: "",
          display_name: "",
          input_token_limit: 0,
          description: "",
          feature_flags: {
            supports_images: false,
            supports_streaming: false,
            supports_temperature: false,
            is_reasoning_model: false,
            supports_thinking_streaming: false,
            supports_system_message: false,
            supports_max_completion_tokens: false,
            supports_reasoning_efforts: false,
            supports_tool_calls: false,
          },
        }
    }

    return { allowedModules, activeModel: activeSettings.chatModel[session_type] }
  }

  getModelsforModule(session_type: string): GPTModelItem[] {
    return (this.settings.models ?? []).filter(
      (m) =>
        (!m.requiredModules ||
          m.requiredModules.length === 0 ||
          m.requiredModules.includes(session_type)) &&
        (!m.blockedModules || !m.blockedModules.includes(session_type))
    )
  }
}
